// client/src/components/pages/ProductMovement/index.tsx (converted from .js, ST-00216)
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FaBox,
  FaArrowRight,
  FaRoute,
  FaChevronDown,
  FaMapMarkerAlt,
  FaSearch,
  FaChartLine,
  FaList,
  FaExclamationCircle,
} from 'react-icons/fa';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LineChart,
  Line,
  Treemap,
} from 'recharts';
import api from '../../../services/api';
import UnifiedDateRangePicker from '../../common/UnifiedDateRangePicker';
import { PageHeader, SegmentedControl } from '../../../types/commonComponents';
import { toApiDateString } from '../../../utils/dateUtils';
import { useThemeTokens } from '../../../utils/themeTokens';
import { formatTonnes } from './formatTonnes';
import ProductTreemapTile from './ProductTreemapTile';
import type {
  MovementTicketType,
  ProductByGroupingRow,
  ProductByGroupingTotals,
  ProductGroupingRow,
  ProductSummaryRow,
  ProductTrendRow,
} from '../../../types/clientPages/productMovement';

const COLORS = [
  '#45B7AA',
  '#E8655A',
  '#F5A623',
  '#4A90D9',
  '#8B5CF6',
  '#EC4899',
  '#10B981',
  '#F59E0B',
  '#6366F1',
  '#14B8A6',
];

/** The server's reason when it sent one, else the transport error, else `fallback`. */
const loadErrorMessage = (err: unknown, fallback: string): string => {
  const e = err as {
    response?: { data?: { error?: string; message?: string } };
    message?: string;
  } | null;
  return e?.response?.data?.error || e?.response?.data?.message || e?.message || fallback;
};

/** A failed load must never read as "nothing moved" (ST-00215: ANL-18). */
const ErrorBanner = ({ message, onRetry }: { message: string; onRetry?: () => void }) => (
  <div
    role="alert"
    className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg flex items-center justify-between gap-3"
  >
    <span className="flex items-center gap-2">
      <FaExclamationCircle className="flex-shrink-0" />
      {message}
    </span>
    {onRetry && (
      <button
        type="button"
        onClick={onRetry}
        className="px-2.5 py-1 rounded border border-red-300 text-red-700 hover:bg-red-100 font-medium"
      >
        Retry
      </button>
    )}
  </div>
);

/** Trend cache key: a grouping's trend differs per ticket type (ANL-2). */
const trendKey = (ticketType: string, groupingId: number | string): string =>
  `${ticketType}:${groupingId}`;

const LoadingSkeleton = () => (
  <div className="animate-pulse space-y-3">
    {[...Array(4)].map((_, i) => (
      <div key={i} className="bg-surface rounded-lg p-4 border border-gray-100">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 bg-gray-200 rounded-lg" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-200 rounded w-1/3" />
            <div className="h-3 bg-gray-100 rounded w-1/2" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

// ───────────────────────────────────────────────────────
//  Shared: ProductBar
// ───────────────────────────────────────────────────────
interface ProductBarProps {
  product: string;
  tonnage: number;
  maxTonnage: number;
  tickets: number;
  color: string | undefined;
}

const ProductBar = ({ product, tonnage, maxTonnage, tickets, color }: ProductBarProps) => {
  const pct = maxTonnage > 0 ? (tonnage / maxTonnage) * 100 : 0;
  return (
    <div className="flex items-center gap-2 py-1.5">
      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-xs font-medium text-slate-800 truncate">{product}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${pct}%`, backgroundColor: color }}
            />
          </div>
          <span className="text-[11px] font-mono text-gray-500 w-16 text-right flex-shrink-0">
            {formatTonnes(tonnage)}t
          </span>
          <span className="text-[10px] text-gray-400 w-14 text-right flex-shrink-0 hidden sm:block">
            {tickets.toLocaleString()} tkt
          </span>
        </div>
      </div>
    </div>
  );
};

// ───────────────────────────────────────────────────────
//  Groupings View: Card
// ───────────────────────────────────────────────────────
interface TicketTypeToggleProps {
  value: MovementTicketType;
  onChange: (type: MovementTicketType) => void;
  size?: 'sm' | 'lg';
}

const TicketTypeToggle = ({ value, onChange, size = 'sm' }: TicketTypeToggleProps) => {
  const sizeClasses = size === 'lg' ? 'px-4 py-1.5 text-xs' : 'px-2.5 py-1 text-[10px]';
  return (
    <div className={`flex items-center bg-gray-100 rounded-md p-0.5 font-medium`}>
      {(['Dispatch', 'Receipt'] as const).map((opt) => (
        <button
          key={opt}
          onClick={(e) => {
            e.stopPropagation();
            onChange(opt);
          }}
          className={`${sizeClasses} rounded transition-all ${
            value === opt
              ? 'bg-surface text-slate-800 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
};

/** One product's totals on a grouping card, per ticket type. */
interface GroupingTypeProduct {
  product: string;
  totalNett: number;
  tickets: number;
}

/** One product's totals across both ticket types on a grouping card. */
interface GroupingProduct extends GroupingTypeProduct {
  avgNett: number;
  activeDays: number;
  _count: number;
}

/** A route grouping card, aggregated from the by-grouping rows. */
interface GroupingAggregate {
  id: number;
  name: string;
  /** The grouping's raw JSON array strings. */
  dispatchLocations: string | null;
  receiptLocations: string | null;
  bfcfMatchMode?: string | null;
  /** Only while it is being built. */
  _raw?: ProductByGroupingRow[];
  products: GroupingProduct[];
  productsByType: Partial<Record<MovementTicketType, GroupingTypeProduct[]>>;
  totalNett: number;
  totalTickets: number;
}

interface GroupingCardProps {
  grouping: GroupingAggregate;
  isExpanded: boolean;
  onToggle: () => void;
  trendData: ProductTrendRow[] | undefined;
  trendLoading: boolean | undefined;
  trendError: string | null | undefined;
  onLoadTrend: (groupingId: number) => void;
  ticketType: MovementTicketType;
}

interface DailyTrendPoint {
  date: string;
  totalNett: number;
  tickets: number;
}

const GroupingCard = ({
  grouping,
  isExpanded,
  onToggle,
  trendData,
  trendLoading,
  trendError,
  onLoadTrend,
  ticketType,
}: GroupingCardProps) => {
  const t = useThemeTokens();
  const dispatch = useMemo((): string[] => {
    try {
      return JSON.parse(grouping.dispatchLocations || '[]');
    } catch {
      return [];
    }
  }, [grouping.dispatchLocations]);

  const receipt = useMemo((): string[] => {
    try {
      return JSON.parse(grouping.receiptLocations || '[]');
    } catch {
      return [];
    }
  }, [grouping.receiptLocations]);

  const products = useMemo(() => {
    return grouping.productsByType[ticketType] || [];
  }, [grouping, ticketType]);

  const filteredTotals = useMemo(() => {
    let nett = 0,
      tkt = 0;
    products.forEach((p) => {
      nett += p.totalNett;
      tkt += p.tickets;
    });
    return { totalNett: nett, totalTickets: tkt };
  }, [products]);

  const maxTonnage = products.length > 0 ? products[0]!.totalNett : 0;

  const chartData = useMemo(() => {
    return products.slice(0, 8).map((p) => ({
      name: p.product.length > 18 ? p.product.substring(0, 18) + '…' : p.product,
      totalNett: p.totalNett,
      tickets: p.tickets,
    }));
  }, [products]);

  const dailyTrend = useMemo(() => {
    if (!trendData) return [];
    const byDate: Record<string, DailyTrendPoint> = {};
    trendData.forEach((r) => {
      const d = toApiDateString(new Date(r.TicketDate));
      if (!byDate[d]) byDate[d] = { date: d, totalNett: 0, tickets: 0 };
      byDate[d]!.totalNett += r.totalNett || 0;
      byDate[d]!.tickets += r.tickets || 0;
    });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  }, [trendData]);

  return (
    <div
      className={`bg-surface rounded-lg border transition-all duration-300 ease-in-out ${isExpanded ? 'border-primary-500 shadow-md' : 'border-gray-100 shadow-sm hover:border-gray-200'}`}
    >
      <div className="px-4 py-3 cursor-pointer" onClick={onToggle}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-primary-500/10 text-primary-500">
            <FaRoute className="text-sm" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-slate-800 truncate">{grouping.name}</h3>
            <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-gray-400">
              <FaMapMarkerAlt className="text-[8px] text-orange-400 flex-shrink-0" />
              <span className="text-gray-500 truncate">{dispatch.join(', ')}</span>
              <FaArrowRight className="text-[8px] text-gray-300 flex-shrink-0" />
              <FaMapMarkerAlt className="text-[8px] text-blue-400 flex-shrink-0" />
              <span className="text-gray-500 truncate">{receipt.join(', ')}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 pl-3">
            <div className="text-right hidden sm:flex flex-col items-end whitespace-nowrap">
              <span className="text-base font-bold text-slate-800 tabular-nums leading-tight">
                {formatTonnes(filteredTotals.totalNett)}t
              </span>
              <span className="text-[10px] text-gray-400 leading-tight">
                {filteredTotals.totalTickets.toLocaleString()} tickets
              </span>
            </div>
            <span className="sm:hidden text-xs font-bold text-slate-800 whitespace-nowrap">
              {formatTonnes(filteredTotals.totalNett)}t
            </span>
            <FaChevronDown
              className={`text-gray-300 text-[10px] transition-transform duration-300 ml-1 ${isExpanded ? 'rotate-180' : ''}`}
            />
          </div>
        </div>

        {/* Collapsed preview bar */}
        <div
          className="transition-all duration-300 ease-in-out overflow-hidden"
          style={{ maxHeight: isExpanded ? 0 : 40, opacity: isExpanded ? 0 : 1 }}
        >
          {products.length > 0 && (
            <div className="flex items-center gap-0.5 mt-2.5 ml-12 mr-1">
              <div className="flex items-center gap-0.5 flex-1 h-2.5 rounded-full overflow-hidden">
                {products.slice(0, 6).map((p, i) => {
                  const pct =
                    filteredTotals.totalNett > 0
                      ? Math.max((p.totalNett / filteredTotals.totalNett) * 100, 3)
                      : 0;
                  return (
                    <div
                      key={i}
                      style={{ flex: pct, backgroundColor: COLORS[i % COLORS.length] }}
                      className="h-full first:rounded-l-full last:rounded-r-full"
                      title={`${p.product}: ${formatTonnes(p.totalNett)}t`}
                    />
                  );
                })}
              </div>
              <span className="text-[10px] text-gray-400 ml-2 flex-shrink-0">
                {products.length} product{products.length !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Expanded detail */}
      <div
        className="transition-all duration-400 ease-in-out overflow-hidden"
        style={{ maxHeight: isExpanded ? 1200 : 0, opacity: isExpanded ? 1 : 0 }}
      >
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="flex items-center justify-between mt-3 mb-2">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">
              Products ({products.length}){' '}
              <span className="ml-1 normal-case">
                — {ticketType} · {formatTonnes(filteredTotals.totalNett)}t ·{' '}
                {filteredTotals.totalTickets.toLocaleString()} tkt
              </span>
            </p>
          </div>

          {products.length > 0 ? (
            <div className="space-y-0.5">
              {products.map((p, i) => (
                <ProductBar
                  key={`${p.product}-${i}`}
                  product={p.product}
                  tonnage={p.totalNett}
                  maxTonnage={maxTonnage}
                  tickets={p.tickets}
                  color={COLORS[i % COLORS.length]}
                />
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 py-4 text-center">
              No {ticketType.toLowerCase()} tickets for this route
            </p>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            {chartData.length > 1 && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-2">
                  Volume Breakdown
                </p>
                <ResponsiveContainer width="100%" height={Math.max(140, chartData.length * 28)}>
                  <BarChart
                    data={chartData}
                    layout="vertical"
                    margin={{ left: 0, right: 10, top: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={t.grid} />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 9, fill: t.axisLabel }}
                      tickFormatter={formatTonnes}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={130}
                      tick={{ fontSize: 10, fill: t.textMuted }}
                    />
                    <Tooltip
                      formatter={(v) => [`${formatTonnes(v as number)}t`, 'Tonnage']}
                      contentStyle={{
                        fontSize: 11,
                        borderRadius: 8,
                        background: t.tooltipBg,
                        border: `1px solid ${t.tooltipBorder}`,
                        color: t.tooltipText,
                      }}
                    />
                    <Bar dataKey="totalNett" radius={[0, 4, 4, 0]} isAnimationActive={false}>
                      {chartData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">
                  Daily Trend
                </p>
                {!trendData && !trendLoading && (
                  <button
                    onClick={() => onLoadTrend(grouping.id)}
                    className="text-[10px] text-primary-500 hover:underline flex items-center gap-1"
                  >
                    <FaChartLine className="text-[9px]" /> {trendError ? 'Retry' : 'Load'}
                  </button>
                )}
              </div>
              {trendLoading ? (
                <div className="animate-pulse h-32 bg-gray-200 rounded" />
              ) : trendError ? (
                <p role="alert" className="text-xs text-red-600 text-center py-8">
                  {trendError}
                </p>
              ) : dailyTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height={140}>
                  <LineChart data={dailyTrend} margin={{ left: 0, right: 10, top: 5, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={t.grid} />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 9, fill: t.axisLabel }}
                      tickFormatter={(v) => {
                        const dt = new Date(v);
                        return `${dt.getDate()}/${dt.getMonth() + 1}`;
                      }}
                    />
                    <YAxis tick={{ fontSize: 9, fill: t.axisLabel }} tickFormatter={formatTonnes} />
                    <Tooltip
                      labelFormatter={(v) =>
                        new Date(v).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })
                      }
                      formatter={(v) => [`${formatTonnes(v as number)}t`, 'Tonnage']}
                      contentStyle={{
                        fontSize: 11,
                        borderRadius: 8,
                        background: t.tooltipBg,
                        border: `1px solid ${t.tooltipBorder}`,
                        color: t.tooltipText,
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="totalNett"
                      stroke="#45B7AA"
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : trendData ? (
                <p className="text-xs text-gray-400 text-center py-8">No trend data</p>
              ) : (
                <p className="text-xs text-gray-400 text-center py-8">
                  Click "Load" to see daily {ticketType.toLowerCase()} tonnage
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ───────────────────────────────────────────────────────
//  Products View
// ───────────────────────────────────────────────────────
interface DateRangeProps {
  /** YYYY-MM-DD */
  startDate: string;
  /** YYYY-MM-DD */
  endDate: string;
}

/** A route grouping in the consolidated view, from the product-groupings rows. */
interface ProductRouteGrouping {
  id: number;
  name: string;
  dispatchLocations: string[];
  receiptLocations: string[];
  totalNett: number;
  tickets: number;
  products: Record<string, { totalNett: number; tickets: number }>;
}

/** One day of the per-product trend: `date` plus one tonnage column per selected product. */
type TrendByProductPoint = { date: string } & Record<string, number | string>;

const ProductsView = ({ startDate, endDate }: DateRangeProps) => {
  const t = useThemeTokens();
  const [products, setProducts] = useState<ProductSummaryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [trendData, setTrendData] = useState<ProductTrendRow[] | null>(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [groupingsData, setGroupingsData] = useState<ProductGroupingRow[] | null>(null);
  const [groupingsLoading, setGroupingsLoading] = useState(false);
  const [ticketType, setTicketType] = useState<MovementTicketType>('Dispatch');
  const [error, setError] = useState<string | null>(null);
  const [trendError, setTrendError] = useState<string | null>(null);
  const [groupingsError, setGroupingsError] = useState<string | null>(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.productMovement.getProducts({ startDate, endDate, ticketType });
      setProducts(data as ProductSummaryRow[]);
      setSelected(new Set());
      setTrendData(null);
      setGroupingsData(null);
    } catch (err) {
      console.error('Failed to fetch products:', err);
      setProducts([]);
      setError(loadErrorMessage(err, 'Could not load product movement'));
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, ticketType]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const totals = useMemo(() => {
    if (!products.length) return { tonnes: 0, tickets: 0, products: 0 };
    return {
      tonnes: products.reduce((s, p) => s + (p.totalNett || 0), 0),
      tickets: products.reduce((s, p) => s + (p.tickets || 0), 0),
      products: products.length,
    };
  }, [products]);

  const filtered = useMemo(() => {
    if (!searchTerm) return products;
    const term = searchTerm.toLowerCase();
    return products.filter((p) => p.Product?.toLowerCase().includes(term));
  }, [products, searchTerm]);

  const treemapData = useMemo(() => {
    return products.slice(0, 20).map((p, i) => ({
      name: p.Product,
      size: p.totalNett || 0,
      tonnage: p.totalNett || 0,
      tickets: p.tickets,
      fill: COLORS[i % COLORS.length],
    }));
  }, [products]);

  const toggleSelect = useCallback((productName: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(productName)) next.delete(productName);
      else next.add(productName);
      return next;
    });
  }, []);

  const selectedTotals = useMemo(() => {
    if (selected.size === 0) return null;
    let tonnes = 0,
      tickets = 0;
    products
      .filter((p) => selected.has(p.Product))
      .forEach((p) => {
        tonnes += p.totalNett || 0;
        tickets += p.tickets || 0;
      });
    return { tonnes, tickets, count: selected.size };
  }, [products, selected]);

  const fetchTrend = useCallback(async () => {
    if (selected.size === 0) return;
    setTrendLoading(true);
    setTrendError(null);
    try {
      const prods = Array.from(selected);
      // Same ticket type as the totals above it, not dispatch + receipt (ANL-2).
      const { data } = await api.productMovement.getTrend({
        startDate,
        endDate,
        products: prods.join(','),
        ticketType,
      });
      setTrendData(data as ProductTrendRow[]);
    } catch (err) {
      console.error('Failed to fetch trend:', err);
      setTrendData(null);
      setTrendError(loadErrorMessage(err, 'Could not load the daily trend'));
    } finally {
      setTrendLoading(false);
    }
  }, [startDate, endDate, selected, ticketType]);

  const fetchGroupings = useCallback(async () => {
    if (selected.size === 0) return;
    setGroupingsLoading(true);
    setGroupingsError(null);
    try {
      const prods = Array.from(selected);
      const { data } = await api.productMovement.getProductGroupings({
        startDate,
        endDate,
        products: prods.join(','),
        ticketType,
      });
      setGroupingsData(data as ProductGroupingRow[]);
    } catch (err) {
      console.error('Failed to fetch product groupings:', err);
      setGroupingsData(null);
      setGroupingsError(loadErrorMessage(err, 'Could not load the route groupings'));
    } finally {
      setGroupingsLoading(false);
    }
  }, [startDate, endDate, selected, ticketType]);

  useEffect(() => {
    if (selected.size > 0) {
      fetchTrend();
      fetchGroupings();
    } else {
      setTrendData(null);
      setGroupingsData(null);
      setTrendError(null);
      setGroupingsError(null);
    }
  }, [selected, fetchTrend, fetchGroupings]);

  const selectedProducts = useMemo(() => {
    if (selected.size === 0) return [];
    return products
      .filter((p) => selected.has(p.Product))
      .sort((a, b) => b.totalNett - a.totalNett);
  }, [products, selected]);

  const routeGroupings = useMemo(() => {
    if (!groupingsData) return [];
    const map: Record<number, ProductRouteGrouping> = {};
    groupingsData.forEach((r) => {
      const gid = r.GroupingID;
      if (!map[gid]) {
        let dispLocs: string[] = [],
          recLocs: string[] = [];
        try {
          dispLocs = JSON.parse(r.DispatchLocations || '[]');
        } catch {}
        try {
          recLocs = JSON.parse(r.ReceiptLocations || '[]');
        } catch {}
        map[gid] = {
          id: gid,
          name: r.GroupingName,
          dispatchLocations: dispLocs,
          receiptLocations: recLocs,
          totalNett: 0,
          tickets: 0,
          products: {},
        };
      }
      map[gid]!.totalNett += r.totalNett || 0;
      map[gid]!.tickets += r.tickets || 0;
      if (!map[gid]!.products[r.Product])
        map[gid]!.products[r.Product] = { totalNett: 0, tickets: 0 };
      map[gid]!.products[r.Product]!.totalNett += r.totalNett || 0;
      map[gid]!.products[r.Product]!.tickets += r.tickets || 0;
    });
    return Object.values(map).sort((a, b) => b.totalNett - a.totalNett);
  }, [groupingsData]);

  const trendByProduct = useMemo(() => {
    if (!trendData || selected.size === 0) return [];
    const dateMap: Record<string, TrendByProductPoint> = {};
    const prodList = Array.from(selected);
    trendData.forEach((r) => {
      const d = toApiDateString(new Date(r.TicketDate));
      if (!dateMap[d]) {
        dateMap[d] = { date: d };
        prodList.forEach((p) => {
          dateMap[d]![p] = 0;
        });
      }
      // The selected products' columns hold numbers.
      if (dateMap[d]![r.Product] !== undefined)
        (dateMap[d]![r.Product] as number) += r.totalNett || 0;
    });
    return Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
  }, [trendData, selected]);

  if (loading) return <LoadingSkeleton />;

  return (
    <>
      {error && <ErrorBanner message={error} onRetry={fetchProducts} />}

      {/* Ticket Type + Treemap */}
      <div className="bg-surface rounded-lg shadow-sm border border-gray-100 p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-slate-800">Product Volume Distribution</h2>
          <TicketTypeToggle value={ticketType} onChange={setTicketType} />
        </div>
        {treemapData.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={260}>
              <Treemap
                data={treemapData}
                dataKey="size"
                content={<ProductTreemapTile />}
                onClick={(node) => node?.name && toggleSelect(node.name)}
                style={{ cursor: 'pointer' }}
              />
            </ResponsiveContainer>
            {selected.size === 0 && (
              <p className="text-[10px] text-gray-400 text-center mt-2">
                Click products on the chart or use checkboxes below to select and compare
              </p>
            )}
          </>
        ) : error ? null : (
          <p className="text-sm text-gray-400 text-center py-8">No product data for this period</p>
        )}
      </div>

      {/* Product picker — click chips to select (always above consolidated so clicks don't shift the picker itself) */}
      <div className="bg-surface rounded-lg shadow-sm border border-gray-100 p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">Select Products to Compare</h2>
            <p className="text-[10px] text-gray-400 mt-0.5">
              Click products that are the same (e.g. different naming conventions) to see their
              combined totals
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {selected.size > 0 && (
              <button
                onClick={() => setSelected(new Set())}
                className="text-[10px] text-gray-500 hover:text-gray-700 border border-gray-200 rounded px-2 py-1"
              >
                Clear
              </button>
            )}
            <div className="relative">
              <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-300 text-[10px]" />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search products…"
                className="pl-7 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600/30 focus:border-primary-500 w-48"
              />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {filtered.map((p, i) => {
            const isSelected = selected.has(p.Product);
            const color = COLORS[i % COLORS.length];
            return (
              <button
                key={p.Product}
                onClick={() => toggleSelect(p.Product)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium border transition-colors duration-200 ${
                  isSelected
                    ? 'bg-primary-500/10 border-primary-500 text-slate-800 shadow-sm ring-1 ring-primary-600/20'
                    : 'bg-surface border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0 transition-colors duration-200"
                  style={{
                    backgroundColor: isSelected ? '#45B7AA' : color,
                    opacity: isSelected ? 1 : 0.5,
                  }}
                />
                {p.Product}
                <span className="text-[9px] font-mono text-gray-400 ml-0.5">
                  {formatTonnes(p.totalNett)}t
                </span>
                {isSelected && <span className="text-primary-500 ml-0.5">✓</span>}
              </button>
            );
          })}
        </div>
        {filtered.length === 0 && (
          <p className="text-center text-gray-400 text-sm py-4">No products match your search</p>
        )}
      </div>

      {/* Consolidated view — always rendered, animates open/closed via max-height */}
      <div
        className="mb-4 overflow-hidden transition-all duration-500 ease-in-out"
        style={{
          maxHeight: selectedTotals ? 3000 : 0,
          opacity: selectedTotals ? 1 : 0,
        }}
      >
        <div className="bg-primary-500/5 border border-primary-500/20 rounded-lg p-4">
          {selectedTotals && (
            <>
              {/* Header with tags */}
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                <div>
                  <p className="text-xs font-semibold text-slate-800">
                    Consolidated View — {selectedTotals.count} product
                    {selectedTotals.count > 1 ? 's' : ''}
                  </p>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    Combined:{' '}
                    <span className="font-mono font-medium">
                      {formatTonnes(selectedTotals.tonnes)}t
                    </span>{' '}
                    · {selectedTotals.tickets.toLocaleString()} tickets
                    {selectedTotals.count > 1 && (
                      <span className="ml-1.5">
                        · avg {formatTonnes(selectedTotals.tonnes / selectedTotals.tickets)}t/ticket
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="flex flex-wrap gap-1.5">
                    {Array.from(selected).map((name) => (
                      <span
                        key={name}
                        onClick={() => toggleSelect(name)}
                        className="text-[10px] bg-surface border border-primary-500/30 text-primary-500 px-2 py-0.5 rounded-full cursor-pointer hover:bg-primary-500/10 flex items-center gap-1 transition-colors duration-200"
                      >
                        {name} <span className="text-gray-400">×</span>
                      </span>
                    ))}
                  </div>
                  <button
                    onClick={() => setSelected(new Set())}
                    className="text-[10px] text-gray-400 hover:text-gray-600 flex-shrink-0"
                  >
                    Clear all
                  </button>
                </div>
              </div>

              {/* Product breakdown bars */}
              {selectedProducts.length > 1 && (
                <div className="bg-surface rounded-lg p-3 mb-3">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-2">
                    Contribution Breakdown
                  </p>
                  <div className="space-y-1.5">
                    {selectedProducts.map((p) => {
                      const pct =
                        selectedTotals.tonnes > 0 ? (p.totalNett / selectedTotals.tonnes) * 100 : 0;
                      const color = COLORS[Array.from(selected).indexOf(p.Product) % COLORS.length];
                      return (
                        <div key={p.Product} className="flex items-center gap-2">
                          <div
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: color }}
                          />
                          <span className="text-[11px] text-slate-800 w-32 sm:w-48 truncate flex-shrink-0">
                            {p.Product}
                          </span>
                          <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-500 ease-out"
                              style={{ width: `${pct}%`, backgroundColor: color }}
                            />
                          </div>
                          <span className="text-[11px] font-mono text-gray-500 w-16 text-right flex-shrink-0">
                            {formatTonnes(p.totalNett)}t
                          </span>
                          <span className="text-[10px] text-gray-400 w-12 text-right flex-shrink-0">
                            {pct.toFixed(1)}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  {/* Stacked summary bar */}
                  <div className="flex items-center gap-0 mt-3 rounded-full overflow-hidden h-3">
                    {selectedProducts.map((p) => {
                      const pct =
                        selectedTotals.tonnes > 0 ? (p.totalNett / selectedTotals.tonnes) * 100 : 0;
                      const color = COLORS[Array.from(selected).indexOf(p.Product) % COLORS.length];
                      return (
                        <div
                          key={p.Product}
                          className="h-full transition-all duration-500 ease-out"
                          style={{ width: `${pct}%`, backgroundColor: color }}
                          title={`${p.Product}: ${pct.toFixed(1)}%`}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {(trendError || groupingsError) && (
                <ErrorBanner
                  message={[trendError, groupingsError].filter(Boolean).join(' · ')}
                  onRetry={() => {
                    if (trendError) fetchTrend();
                    if (groupingsError) fetchGroupings();
                  }}
                />
              )}

              {/* Trend chart */}
              {trendLoading ? (
                <div className="animate-pulse h-44 bg-surface rounded" />
              ) : (
                trendByProduct.length > 0 && (
                  <div className="bg-surface rounded-lg p-3">
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-2">
                      {selected.size > 1 ? 'Daily Trend by Product' : 'Daily Trend'}
                    </p>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart
                        data={trendByProduct}
                        margin={{ left: 0, right: 10, top: 5, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={t.grid} />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 9, fill: t.axisLabel }}
                          tickFormatter={(v) => {
                            const dt = new Date(v);
                            return `${dt.getDate()}/${dt.getMonth() + 1}`;
                          }}
                        />
                        <YAxis
                          tick={{ fontSize: 9, fill: t.axisLabel }}
                          tickFormatter={formatTonnes}
                        />
                        <Tooltip
                          labelFormatter={(v) =>
                            new Date(v).toLocaleDateString('en-ZA', {
                              day: 'numeric',
                              month: 'short',
                            })
                          }
                          formatter={(v, name) => [`${formatTonnes(v as number)}t`, name]}
                          contentStyle={{
                            fontSize: 11,
                            borderRadius: 8,
                            background: t.tooltipBg,
                            border: `1px solid ${t.tooltipBorder}`,
                            color: t.tooltipText,
                          }}
                        />
                        {Array.from(selected).map((name, i) => (
                          <Line
                            key={name}
                            type="monotone"
                            dataKey={name}
                            stroke={COLORS[i % COLORS.length]}
                            strokeWidth={2}
                            dot={false}
                            isAnimationActive={false}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )
              )}

              {/* Route Groupings */}
              {groupingsLoading ? (
                <div className="animate-pulse h-32 bg-surface rounded mt-3" />
              ) : (
                routeGroupings.length > 0 && (
                  <div className="bg-surface rounded-lg p-3 mt-3">
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-2">
                      Route Groupings ({routeGroupings.length})
                    </p>
                    <div className="space-y-2">
                      {routeGroupings.map((g) => {
                        const pct =
                          selectedTotals && selectedTotals.tonnes > 0
                            ? (g.totalNett / selectedTotals.tonnes) * 100
                            : 0;
                        const productEntries = Object.entries(g.products).sort(
                          (a, b) => b[1].totalNett - a[1].totalNett
                        );
                        return (
                          <div key={g.id} className="border border-gray-100 rounded-lg p-2.5">
                            <div className="flex items-center gap-2 mb-1.5">
                              <FaRoute className="text-[10px] text-primary-500 flex-shrink-0" />
                              <span className="text-[11px] font-semibold text-slate-800 truncate">
                                {g.name}
                              </span>
                              <span className="text-[10px] font-mono text-gray-500 ml-auto flex-shrink-0">
                                {formatTonnes(g.totalNett)}t
                              </span>
                              <span className="text-[9px] text-gray-400 flex-shrink-0">
                                {g.tickets.toLocaleString()} tkt
                              </span>
                            </div>
                            <div className="flex items-center gap-1 text-[10px] text-gray-400 mb-1.5">
                              <FaMapMarkerAlt className="text-[7px] text-orange-400" />
                              <span className="truncate">{g.dispatchLocations.join(', ')}</span>
                              <FaArrowRight className="text-[7px] text-gray-300 flex-shrink-0" />
                              <FaMapMarkerAlt className="text-[7px] text-blue-400" />
                              <span className="truncate">{g.receiptLocations.join(', ')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                                <div
                                  className="h-full rounded-full bg-primary-500"
                                  style={{ width: `${Math.min(pct, 100)}%` }}
                                />
                              </div>
                              <span className="text-[9px] text-gray-400 w-10 text-right">
                                {pct.toFixed(1)}%
                              </span>
                            </div>
                            {selected.size > 1 && productEntries.length > 1 && (
                              <div className="mt-1.5 flex flex-wrap gap-1">
                                {productEntries.map(([name, data]) => (
                                  <span
                                    key={name}
                                    className="text-[9px] bg-gray-50 border border-gray-100 text-gray-500 px-1.5 py-0.5 rounded"
                                  >
                                    {name}:{' '}
                                    <span className="font-mono">
                                      {formatTonnes(data.totalNett)}t
                                    </span>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )
              )}

              {/* Stats comparison table */}
              {selectedProducts.length > 1 && (
                <div className="bg-surface rounded-lg overflow-hidden mt-3">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="bg-gray-50 text-left">
                        <th className="px-3 py-2 font-medium text-gray-500">Product</th>
                        <th className="px-3 py-2 font-medium text-gray-500 text-right">Tonnage</th>
                        <th className="px-3 py-2 font-medium text-gray-500 text-right">Tickets</th>
                        <th className="px-3 py-2 font-medium text-gray-500 text-right">
                          Avg Weight
                        </th>
                        <th className="px-3 py-2 font-medium text-gray-500 text-right hidden sm:table-cell">
                          Days
                        </th>
                        <th className="px-3 py-2 font-medium text-gray-500 text-right">Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedProducts.map((p) => {
                        const pct =
                          selectedTotals.tonnes > 0
                            ? (p.totalNett / selectedTotals.tonnes) * 100
                            : 0;
                        const color =
                          COLORS[Array.from(selected).indexOf(p.Product) % COLORS.length];
                        return (
                          <tr key={p.Product} className="border-t border-gray-50">
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-1.5">
                                <div
                                  className="w-2 h-2 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: color }}
                                />
                                <span className="font-medium text-slate-800 truncate">
                                  {p.Product}
                                </span>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right font-mono">
                              {formatTonnes(p.totalNett)}t
                            </td>
                            <td className="px-3 py-2 text-right font-mono">
                              {p.tickets.toLocaleString()}
                            </td>
                            <td className="px-3 py-2 text-right font-mono">
                              {p.avgNett?.toFixed(2)}t
                            </td>
                            <td className="px-3 py-2 text-right hidden sm:table-cell">
                              {p.activeDays}
                            </td>
                            <td className="px-3 py-2 text-right font-mono">{pct.toFixed(1)}%</td>
                          </tr>
                        );
                      })}
                      <tr className="border-t-2 border-primary-500/20 bg-primary-500/5 font-semibold">
                        <td className="px-3 py-2 text-slate-800">Combined Total</td>
                        <td className="px-3 py-2 text-right font-mono text-slate-800">
                          {formatTonnes(selectedTotals.tonnes)}t
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-slate-800">
                          {selectedTotals.tickets.toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-slate-800">
                          {selectedTotals.tickets > 0
                            ? (selectedTotals.tonnes / selectedTotals.tickets).toFixed(2)
                            : '—'}
                          t
                        </td>
                        <td className="px-3 py-2 text-right hidden sm:table-cell">—</td>
                        <td className="px-3 py-2 text-right font-mono text-slate-800">100%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* All Products detail table */}
      <div className="bg-surface rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-slate-800">All Products</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="px-4 py-2.5 font-medium text-gray-500">Product</th>
                <th className="px-4 py-2.5 font-medium text-gray-500 text-right">Tonnage</th>
                <th className="px-4 py-2.5 font-medium text-gray-500 text-right">Tickets</th>
                <th className="px-4 py-2.5 font-medium text-gray-500 text-right">Avg Weight</th>
                <th className="px-4 py-2.5 font-medium text-gray-500 text-right hidden sm:table-cell">
                  Locations
                </th>
                <th className="px-4 py-2.5 font-medium text-gray-500 text-right hidden sm:table-cell">
                  Days
                </th>
                <th className="px-4 py-2.5 font-medium text-gray-500 w-28">Share</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => {
                const share = totals.tonnes > 0 ? (p.totalNett / totals.tonnes) * 100 : 0;
                const isSelected = selected.has(p.Product);
                return (
                  <tr
                    key={p.Product}
                    onClick={() => toggleSelect(p.Product)}
                    className={`border-t border-gray-50 cursor-pointer transition-colors ${isSelected ? 'bg-primary-500/5 border-l-2 border-l-primary-600' : 'hover:bg-gray-50'}`}
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: COLORS[i % COLORS.length] }}
                        />
                        <span className="font-medium text-slate-800">{p.Product}</span>
                        {isSelected && (
                          <span className="text-primary-500 text-[10px]">✓ selected</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono">
                      {formatTonnes(p.totalNett)}t
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono">
                      {p.tickets.toLocaleString()}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono">{p.avgNett?.toFixed(2)}t</td>
                    <td className="px-4 py-2.5 text-right hidden sm:table-cell">
                      <span className="text-gray-400">{p.dispatchLocations}D</span>
                      <span className="mx-1 text-gray-300">/</span>
                      <span className="text-gray-400">{p.receiptLocations}R</span>
                    </td>
                    <td className="px-4 py-2.5 text-right hidden sm:table-cell text-gray-500">
                      {p.activeDays}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${Math.min(share, 100)}%`,
                              backgroundColor: COLORS[i % COLORS.length],
                            }}
                          />
                        </div>
                        <span className="text-gray-400 w-10 text-right">{share.toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <p className="text-center text-gray-400 text-sm py-8">No products match your filter</p>
        )}
      </div>
    </>
  );
};

// ───────────────────────────────────────────────────────
//  Groupings View
// ───────────────────────────────────────────────────────
const GroupingsView = ({ startDate, endDate }: DateRangeProps) => {
  const [data, setData] = useState<ProductByGroupingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Trend rows / loading / error per trendKey(ticketType, groupingId).
  const [trends, setTrends] = useState<Record<string, ProductTrendRow[]>>({});
  const [trendLoading, setTrendLoading] = useState<Record<string, boolean>>({});
  const [trendErrors, setTrendErrors] = useState<Record<string, string | null>>({});
  const [ticketType, setTicketType] = useState<MovementTicketType>('Dispatch');
  const [error, setError] = useState<string | null>(null);
  // Header totals per ticket type, each ticket counted once (ANL-17).
  const [headerTotals, setHeaderTotals] = useState<ProductByGroupingTotals | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The header totals are a nice-to-have on top of the cards: if that call
      // fails (or an older server has no such endpoint) the cards still show and
      // the header falls back to adding the cards up (review S6).
      const [cards, totals] = await Promise.allSettled([
        api.productMovement.byGrouping({ startDate, endDate }),
        api.productMovement.byGroupingTotals({ startDate, endDate }),
      ]);
      if (cards.status === 'rejected') throw cards.reason;
      if (totals.status === 'rejected') {
        console.error('Failed to fetch product movement header totals:', totals.reason);
      }
      setData(cards.value.data as ProductByGroupingRow[]);
      setHeaderTotals(
        totals.status === 'fulfilled'
          ? (totals.value.data as ProductByGroupingTotals | null) || {}
          : null
      );
      setTrends({});
      setTrendErrors({});
    } catch (err) {
      console.error('Failed to fetch product movement:', err);
      setData([]);
      setHeaderTotals(null);
      setError(loadErrorMessage(err, 'Could not load product movement'));
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const groupings = useMemo(() => {
    const map: Record<number, GroupingAggregate> = {};
    data.forEach((r) => {
      const gid = r.GroupingID;
      if (!map[gid]) {
        map[gid] = {
          id: gid,
          name: r.GroupingName,
          dispatchLocations: r.DispatchLocations,
          receiptLocations: r.ReceiptLocations,
          bfcfMatchMode: r.BfcfMatchMode,
          _raw: [],
          products: [],
          productsByType: {},
          totalNett: 0,
          totalTickets: 0,
        };
      }
      map[gid]!._raw!.push(r);
    });

    return Object.values(map)
      .map((g) => {
        const byProduct: Record<string, GroupingProduct> = {};
        const byTypeProduct: Partial<
          Record<MovementTicketType, Record<string, GroupingTypeProduct>>
        > = {};
        g._raw!.forEach((r) => {
          const key = r.Product;
          if (!byProduct[key])
            byProduct[key] = {
              product: key,
              totalNett: 0,
              tickets: 0,
              avgNett: 0,
              activeDays: 0,
              _count: 0,
            };
          byProduct[key]!.totalNett += r.totalNett || 0;
          byProduct[key]!.tickets += r.tickets || 0;
          byProduct[key]!._count++;
          byProduct[key]!.avgNett = byProduct[key]!.totalNett / byProduct[key]!.tickets;

          const tt = r.TicketType || 'Other';
          if (tt === 'Dispatch' || tt === 'Receipt') {
            if (!byTypeProduct[tt]) byTypeProduct[tt] = {};
            if (!byTypeProduct[tt]![key])
              byTypeProduct[tt]![key] = { product: key, totalNett: 0, tickets: 0 };
            byTypeProduct[tt]![key]!.totalNett += r.totalNett || 0;
            byTypeProduct[tt]![key]!.tickets += r.tickets || 0;
          }
        });

        g.products = Object.values(byProduct).sort((a, b) => b.totalNett - a.totalNett);
        g.productsByType = {};
        (Object.keys(byTypeProduct) as MovementTicketType[]).forEach((tt) => {
          g.productsByType[tt] = Object.values(byTypeProduct[tt]!).sort(
            (a, b) => b.totalNett - a.totalNett
          );
        });
        g.totalNett = g.products.reduce((s, p) => s + p.totalNett, 0);
        g.totalTickets = g.products.reduce((s, p) => s + p.tickets, 0);
        delete g._raw;
        return g;
      })
      .sort((a, b) => b.totalNett - a.totalNett);
  }, [data]);

  const activeGroupings = useMemo(() => {
    return groupings.filter((g) => g.totalNett > 0);
  }, [groupings]);

  const totals = useMemo(() => {
    const allProducts = new Set<string>();
    let cardTonnes = 0;
    let cardTickets = 0;
    activeGroupings.forEach((g) => {
      (g.productsByType[ticketType] || []).forEach((p) => {
        cardTonnes += p.totalNett;
        cardTickets += p.tickets;
        allProducts.add(p.product);
      });
    });
    // Groupings overlap, so adding up the cards counts a shared ticket once per
    // grouping; the server counts each ticket once (ANL-17). Without its totals
    // (the call failed) the header falls back to the card sum and says so.
    const distinct = headerTotals !== null;
    const typeTotals: { tickets?: number; totalNett?: number } =
      (distinct && headerTotals[ticketType]) || {};
    return {
      tonnes: distinct ? typeTotals.totalNett || 0 : cardTonnes,
      tickets: distinct ? typeTotals.tickets || 0 : cardTickets,
      groupings: activeGroupings.length,
      products: allProducts.size,
      distinct,
    };
  }, [activeGroupings, ticketType, headerTotals]);

  const filtered = useMemo(() => {
    if (!searchTerm) return activeGroupings;
    const term = searchTerm.toLowerCase();
    return activeGroupings.filter((g) => {
      if (g.name.toLowerCase().includes(term)) return true;
      if ((g.dispatchLocations || '').toLowerCase().includes(term)) return true;
      if ((g.receiptLocations || '').toLowerCase().includes(term)) return true;
      const prods = g.productsByType[ticketType] || [];
      return prods.some((p) => p.product.toLowerCase().includes(term));
    });
  }, [activeGroupings, searchTerm, ticketType]);

  // The trend follows the Dispatch / Receipt toggle like the card beside it
  // (ANL-2); results are kept per ticket type, so a late answer for the other
  // type can never land in the wrong chart.
  const loadTrend = useCallback(
    async (groupingId: number, type: MovementTicketType = ticketType) => {
      const key = trendKey(type, groupingId);
      setTrendLoading((prev) => ({ ...prev, [key]: true }));
      setTrendErrors((prev) => ({ ...prev, [key]: null }));
      try {
        const { data: rows } = await api.productMovement.getTrend({
          startDate,
          endDate,
          groupingId,
          ticketType: type,
        });
        setTrends((prev) => ({ ...prev, [key]: rows as ProductTrendRow[] }));
      } catch (err) {
        console.error('Failed to load trend:', err);
        setTrendErrors((prev) => ({
          ...prev,
          [key]: loadErrorMessage(err, 'Could not load the daily trend'),
        }));
      } finally {
        setTrendLoading((prev) => ({ ...prev, [key]: false }));
      }
    },
    [startDate, endDate, ticketType]
  );

  const handleToggle = useCallback(
    (id: number) => {
      setExpandedId((prev) => {
        const newId = prev === id ? null : id;
        if (newId && !trends[trendKey(ticketType, id)]) loadTrend(id);
        return newId;
      });
    },
    [trends, loadTrend, ticketType]
  );

  const handleTicketTypeChange = useCallback(
    (type: MovementTicketType) => {
      setTicketType(type);
      // The open card's trend is for the old type: fetch the new one.
      if (expandedId && !trends[trendKey(type, expandedId)]) loadTrend(expandedId, type);
    },
    [expandedId, trends, loadTrend]
  );

  if (loading) return <LoadingSkeleton />;

  return (
    <>
      {/* Sticky header: toggle + stats + search */}
      <div className="md:sticky md:top-[65px] z-10 -mx-3 sm:-mx-6 px-3 sm:px-6 pb-3 pt-1 bg-gradient-to-b from-gray-50 via-gray-50 to-gray-50/95 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-4">
            <TicketTypeToggle value={ticketType} onChange={handleTicketTypeChange} size="lg" />
            <div
              className="flex items-center gap-3 text-[11px] text-gray-500"
              title={
                totals.distinct
                  ? 'Tickets on more than one route grouping are counted once'
                  : 'Sum of the routes below: a ticket on more than one route grouping is counted once per grouping'
              }
            >
              <span>
                <span className="font-mono font-semibold text-slate-800">
                  {formatTonnes(totals.tonnes)}t
                </span>{' '}
                tonnage
              </span>
              <span className="text-gray-300">·</span>
              <span>
                <span className="font-mono font-semibold text-slate-800">
                  {totals.tickets.toLocaleString()}
                </span>{' '}
                tickets
              </span>
              <span className="text-gray-300 hidden sm:inline">·</span>
              <span className="hidden sm:inline">
                <span className="font-mono font-semibold text-slate-800">{totals.groupings}</span>{' '}
                groupings
              </span>
              <span className="text-gray-300 hidden sm:inline">·</span>
              <span className="hidden sm:inline">
                <span className="font-mono font-semibold text-slate-800">{totals.products}</span>{' '}
                products
              </span>
            </div>
          </div>
        </div>

        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-xs" />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search routes, locations, products…"
            className="w-full pl-9 pr-4 py-2 text-xs border border-gray-200 rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-primary-600/30 focus:border-primary-500"
          />
        </div>
      </div>

      {error && <ErrorBanner message={error} onRetry={fetchData} />}

      {/* Cards */}
      <div className="space-y-2">
        {filtered.map((g) => (
          <GroupingCard
            key={g.id}
            grouping={g}
            isExpanded={expandedId === g.id}
            onToggle={() => handleToggle(g.id)}
            trendData={trends[trendKey(ticketType, g.id)]}
            trendLoading={trendLoading[trendKey(ticketType, g.id)]}
            trendError={trendErrors[trendKey(ticketType, g.id)]}
            onLoadTrend={loadTrend}
            ticketType={ticketType}
          />
        ))}
        {filtered.length === 0 && !error && (
          <div className="bg-surface rounded-lg border border-gray-100 p-8 text-center">
            <FaBox className="text-3xl text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-500">
              {searchTerm
                ? 'No routes match your search'
                : 'No product movement data for this period'}
            </p>
          </div>
        )}
      </div>
    </>
  );
};

// ───────────────────────────────────────────────────────
//  Main Page
// ───────────────────────────────────────────────────────
const toDateStr = (d: Date): string => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ProductMovement = () => {
  const [startDate, setStartDate] = useState<Date>(() => {
    const today = new Date();
    if (today.getDate() === 1) {
      return new Date(today.getFullYear(), today.getMonth() - 1, 1);
    }
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [endDate, setEndDate] = useState<Date>(() => {
    const today = new Date();
    if (today.getDate() === 1) {
      return new Date(today.getFullYear(), today.getMonth(), 0);
    }
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d;
  });
  const [view, setView] = useState<'groupings' | 'products'>('groupings');

  const startStr = toDateStr(startDate);
  const endStr = toDateStr(endDate);

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        icon={FaBox}
        title="Product Movement"
        subtitle={
          view === 'groupings'
            ? 'Products moved per route grouping'
            : 'All products across all routes'
        }
        toolbar={
          <>
            <SegmentedControl
              ariaLabel="Product Movement view"
              value={view}
              onChange={setView}
              options={[
                { value: 'groupings', label: 'Groupings', icon: FaRoute },
                { value: 'products', label: 'Products', icon: FaList },
              ]}
            />
            <div className="sm:w-64">
              <UnifiedDateRangePicker
                startDate={startDate}
                onStartDateChange={setStartDate}
                endDate={endDate}
                onEndDateChange={setEndDate}
              />
            </div>
          </>
        }
      />

      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        {view === 'groupings' ? (
          <GroupingsView startDate={startStr} endDate={endStr} />
        ) : (
          <ProductsView startDate={startStr} endDate={endStr} />
        )}
      </div>
    </div>
  );
};

export default ProductMovement;
